/* assets/product-card-modern.js */
/* Modern product card behavior:
   - Dynamic variant options (supports any number of options)
   - Color/Colour option shown first; rendered as circular swatches
   - Swatch background priority: Theme setting line (hex/image) → matching variant image → asset fallback → gray
   - Availability gating for impossible combos
   - Image swap on variant change (primary only on first load; both on change)
   - AJAX add-to-cart (supports form or button)
   - Works with window.refreshCartUI() from cart-refresh.js
*/
(() => {
  /* ==============================
     Utilities
  ============================== */
  function setBtnLoading(btn, isLoading) {
    if (!btn) return;
    const spin = btn.querySelector('.btn-spinner');
    btn.disabled = !!isLoading;
    btn.classList.toggle('is-loading', !!isLoading);
    if (spin) spin.hidden = !isLoading;
    btn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  async function addToCartAjax(variantId, qty = 1, btn) {
    setBtnLoading(btn, true);
    try {
      // Try modern payload
      let res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: Number(qty) }] }),
      });
      let data = await res.json();

      // Fallback to legacy shape if needed (for some apps/themes)
      if (!res.ok && (data?.description || '').toLowerCase().includes('items')) {
        res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: Number(variantId), quantity: Number(qty) }),
        });
        data = await res.json();
      }
      if (!res.ok) throw new Error(data?.description || 'Add to cart failed');

      btn?.classList.add('is-added');
      if (typeof window.refreshCartUI === 'function') {
        await window.refreshCartUI({ open: true });
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
      setTimeout(() => btn?.classList.remove('is-added'), 1200);
    } catch (err) {
      console.error(err);
      btn?.classList.add('is-error');
      alert(err.message || 'Add to cart failed');
      setTimeout(() => btn?.classList.remove('is-error'), 1200);
    } finally {
      setBtnLoading(btn, false);
    }
  }

  function parseJSON(root, selector) {
    const el = root.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || '');
    } catch {
      return null;
    }
  }

  // Per-line swatch mapping (no JSON). Format per line:
  //   Name = #hex
  //   Name = https://cdn.shopify.com/... (image URL)
  // Comments: lines starting with "#" or "//"
  function readSwatchLines(holder) {
    // Try inside the holder first
    let el = holder?.querySelector?.('.swatch-lines') || null;

    // If not found, try within the nearest product-card container
    if (!el) {
      const card = holder?.closest?.('.product-card-modern') || document.querySelector('.product-card-modern');
      el = card?.querySelector?.('.swatch-lines') || null;
    }

    // As a last resort, try the first one on the page
    if (!el) el = document.querySelector('.swatch-lines');

    // Optional global fallback (inject in theme.liquid if you want)
    // <script>window.__colorSwatchesLines = {{ settings.color_swatches_lines | json }};</script>
    let raw = ((el && el.textContent) || '').trim();
    if (!raw && typeof window.__colorSwatchesLines === 'string') {
      raw = window.__colorSwatchesLines.trim();
    }

    if (!raw) return [];

    const lines = raw
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    const out = [];
    for (const line of lines) {
      if (/^\s*(#|\/\/)/.test(line)) continue;
      const m = line.match(/^(.+?)\s*(?:[:=\-])\s*(.+)$/); // name :|=|- value
      if (!m) continue;
      const name = m[1].trim().replace(/^['"]|['"]$/g, '');
      const val = m[2].trim().replace(/^['"]|['"]$/g, '');
      if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(val)) out.push({ name, hex: val });
      else out.push({ name, image: val }); // expects a full CDN URL if image
    }
    return out;
  }

  function findSwatchByName(swatchList, valueName) {
    const key = (valueName || '').toString().trim().toLowerCase();
    return swatchList.find((x) => (x?.name || '').toString().trim().toLowerCase() === key) || null;
  }

  function buildUniques(options, variants) {
    // returns { "<idx>": ["Red","Blue",...], ... }
    const map = {};
    options.forEach((_, i) => (map[i] = []));
    variants.forEach((v) => {
      v.options.forEach((val, i) => {
        if (!map[i].includes(val)) map[i].push(val);
      });
    });
    return map;
  }

  function variantMatchesSelection(variant, selection) {
    for (let i = 0; i < variant.options.length; i++) {
      if (selection[i] && selection[i] !== variant.options[i]) return false;
    }
    return true;
  }

  function resolveVariantForSelection(variants, selection) {
    const chosenCount = Object.values(selection).filter(Boolean).length;
    const optionsCount = variants[0]?.options?.length || 0;

    if (chosenCount === optionsCount) {
      const exactAvailable = variants.find((v) => v.available && v.options.every((val, i) => selection[i] === val));
      if (exactAvailable) return exactAvailable;
      const exactAny = variants.find((v) => v.options.every((val, i) => selection[i] === val));
      if (exactAny) return exactAny;
    }

    const partial = variants.find((v) => v.available && variantMatchesSelection(v, selection));
    if (partial) return partial;

    return variants[0] || null;
  }

  function computeAvailabilityMatrix(options, variants, selection) {
    const uniques = buildUniques(options, variants);
    const avail = {};
    options.forEach((_, idx) => (avail[idx] = {}));

    options.forEach((_, idx) => {
      uniques[idx].forEach((val) => {
        const ok = variants.some((v) => {
          if (!v.available) return false;
          for (let i = 0; i < options.length; i++) {
            if (i === idx) continue;
            if (selection[i] && selection[i] !== v.options[i]) return false;
          }
          if (v.options[idx] !== val) return false;
          return true;
        });
        avail[idx][val] = ok;
      });
    });

    return avail;
  }

  function findVariantImageForOptionValue(variants, optionIndex, value) {
    for (const v of variants) {
      if (v.options && v.options[optionIndex] === value && v.image) {
        return v.image; // URL built by Liquid
      }
    }
    return '';
  }

  /* ==============================
     Media updates
  ============================== */

  // onlySecondary flag controlled by caller:
  // - initial render: alsoSecondary=false (keep Liquid's different second image)
  // - on option change: alsoSecondary=true (sync both)
  function updateImageForVariant(card, variant, { alsoSecondary = false } = {}) {
    const url = variant?.image || '';
    if (!url) return;

    const primary = card.querySelector('.js-modern-img'); // .modern-img--primary
    const secondary = card.querySelector('.modern-img--secondary'); // optional

    // Always update primary
    if (primary) {
      primary.setAttribute('srcset', '');
      primary.src = url;
    }

    // Update secondary only when explicitly requested
    if (alsoSecondary && secondary) {
      secondary.setAttribute('srcset', '');
      secondary.src = url;
    }
  }

  /* ==============================
     ATC + Options UI state
  ============================== */
  function updateAddToCartForVariant(card, variant) {
    const id = variant?.id ? String(variant.id) : '';
    const input = card.querySelector('.js-variant-id');
    if (input) input.value = id;

    const formBtn = card.querySelector('.modern-add');
    const ajaxBtn = card.querySelector('.js-ajax-add');
    const btn = formBtn || ajaxBtn;

    const enable = !!(variant && variant.available);
    if (btn) {
      if (enable) {
        btn.removeAttribute('disabled');
        btn.classList.remove('is-disabled');
      } else {
        btn.setAttribute('disabled', 'disabled');
        btn.classList.add('is-disabled');
      }
    }
  }

  function markActiveButtons(card, selection) {
    card.querySelectorAll('.option-group').forEach((group) => {
      const idx = Number(group.dataset.optionIndex);
      const current = selection[idx];
      group.querySelectorAll('.option-value').forEach((btn) => {
        const val = btn.dataset.optionValue;
        btn.classList.toggle('is-active', current === val);
      });
    });
  }

  function applyAvailability(card, options, variants, selection) {
    const matrix = computeAvailabilityMatrix(options, variants, selection);
    card.querySelectorAll('.option-value').forEach((btn) => {
      const idx = Number(btn.dataset.optionIndex);
      const val = btn.dataset.optionValue;
      const possible = !!(matrix[idx] && matrix[idx][val]);
      if (possible) {
        btn.classList.remove('is-disabled');
        btn.removeAttribute('disabled');
      } else {
        btn.classList.add('is-disabled');
        btn.setAttribute('disabled', 'disabled');
      }
    });
  }

  function pickInitialSelection(card, options, variants) {
    const selection = {};
    const currentId = card.querySelector('.js-variant-id')?.value;
    let current = currentId ? variants.find((v) => String(v.id) === String(currentId)) : null;
    if (!current) current = variants.find((v) => v.available) || variants[0];
    if (current) current.options.forEach((val, i) => (selection[i] = val));
    return selection;
  }

  /* ==============================
     Rendering & Updates
  ============================== */
  function renderOptionGroups(card) {
    const holder = card.querySelector('.modern-options');
    if (!holder || holder.dataset.init === 'true') return;

    const options = parseJSON(holder, '.product-options') || [];
    const variants = parseJSON(holder, '.product-variants') || [];

    // If no options/variants (simple product case), just mark init and exit.
    if (!options.length || !variants.length) {
      holder.dataset.init = 'true';
      return;
    }

    const swatchList = readSwatchLines(holder);
    const fallbackImg = holder.getAttribute('data-fallback-image') || '';
    const uniques = buildUniques(options, variants);

    // Roots: inline vs hover. Also support legacy single root if present.
    const inlineRoot = holder.querySelector('.option-groups-inline');
    const hoverRoot = holder.querySelector('.option-groups-hover');
    const legacyRoot = holder.querySelector('.option-groups'); // fallback
    if (inlineRoot) inlineRoot.innerHTML = '';
    if (hoverRoot) hoverRoot.innerHTML = '';
    if (legacyRoot) legacyRoot.innerHTML = '';

    // Put "Color/Colour" first visually
    const order = options
      .map((_, i) => i)
      .sort((i, j) => {
        const ai = /colou?r/i.test(options[i]);
        const aj = /colou?r/i.test(options[j]);
        if (ai && !aj) return -1;
        if (!ai && aj) return 1;
        return i - j;
      });

    // Initial selection based on current or first-available variant
    const selection = pickInitialSelection(card, options, variants);

    order.forEach((idx) => {
      const name = options[idx];
      const isColor = /colou?r/i.test(name);
      const isSize = /size/i.test(name);

      const wrap = document.createElement('div');

      // make a safe css class from the option name (e.g. "Material Type" → "material-type")
      const safeName = String(name)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, ''); // trim leading/trailing dashes

      wrap.className = `option-group option-${safeName}`;
      wrap.dataset.optionIndex = String(idx);
      // optional: expose the sanitized name for debugging/styling hooks
      wrap.dataset.optionName = safeName;

      // No label for Color; labels for others
      if (!isColor) {
        const labelDiv = document.createElement('div');
        labelDiv.className = 'option-label';
        labelDiv.textContent = name;
        wrap.appendChild(labelDiv);
      }

      const valuesRoot = document.createElement('div');
      valuesRoot.className = 'option-values';
      wrap.appendChild(valuesRoot);

      (uniques[idx] || []).forEach((val) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'option-value';
        btn.dataset.optionIndex = String(idx);
        btn.dataset.optionValue = val;

        if (isColor) {
          // circle swatch
          btn.classList.add('is-color');
          btn.title = val;

          const dot = document.createElement('span');
          dot.className = 'swatch-dot';

          const map = findSwatchByName(swatchList, val);
          if (map?.hex) {
            dot.style.background = map.hex;
          } else if (map?.image) {
            dot.style.backgroundImage = `url(${map.image})`;
            dot.style.backgroundSize = 'cover';
            dot.style.backgroundPosition = 'center';
          } else {
            const vImg = findVariantImageForOptionValue(variants, idx, val);
            if (vImg) {
              dot.style.backgroundImage = `url(${vImg})`;
              dot.style.backgroundSize = 'cover';
              dot.style.backgroundPosition = 'center';
            } else if (fallbackImg) {
              dot.style.backgroundImage = `url(${fallbackImg})`;
              dot.style.backgroundSize = 'cover';
              dot.style.backgroundPosition = 'center';
            } else {
              dot.style.background = '#e6e6e6';
            }
          }

          const labelSpan = document.createElement('span');
          labelSpan.className = 'label-text';
          labelSpan.textContent = val;

          btn.appendChild(dot);
          btn.appendChild(labelSpan);
        } else if (isSize) {
          // Size = square
          btn.classList.add('is-size');
          btn.textContent = val;
        } else {
          // Others = rectangular/inline pill
          btn.classList.add('is-rect');
          btn.textContent = val;
        }

        if (selection[idx] === val) btn.classList.add('is-active');
        valuesRoot.appendChild(btn);
      });

      // Append to the correct container
      if (inlineRoot || hoverRoot) {
        if (isColor && inlineRoot) inlineRoot.appendChild(wrap);
        else if (hoverRoot) hoverRoot.appendChild(wrap);
        else if (legacyRoot) legacyRoot.appendChild(wrap);
      } else if (legacyRoot) {
        legacyRoot.appendChild(wrap);
      }
    });

    // Persist selection and apply state
    holder._selection = selection;
    markActiveButtons(card, selection);
    applyAvailability(card, options, variants, selection);

    // Initial render: update PRIMARY only; preserve Liquid's secondary
    const variant = resolveVariantForSelection(variants, selection);
    updateImageForVariant(card, variant, { alsoSecondary: false });
    updateAddToCartForVariant(card, variant);
    holder.dataset.init = 'true';
  }

  function reinitWithin(container) {
    container.querySelectorAll('.product-card-modern .modern-options').forEach((h) => {
      h.dataset.init = 'false';
    });
    container.querySelectorAll('.product-card-modern').forEach((card) => renderOptionGroups(card));
  }

  /* ==============================
     Init hooks
  ============================== */
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.product-card-modern').forEach((card) => renderOptionGroups(card));
    // A11Y baseline: any hover panel that starts hidden should be inert (unfocusable)
    document.querySelectorAll('.modern-hover-panel[aria-hidden="true"]').forEach((p) => {
      p.setAttribute('inert', '');
    });
  });

  // Re-render when Shopify loads sections dynamically
  document.addEventListener('shopify:section:load', (evt) => {
    const container = evt.target || document;
    reinitWithin(container);
  });

  // Observe DOM changes (pagination/filters replacing grids)
  const mo = new MutationObserver((records) => {
    let needs = false;
    records.forEach((r) => {
      r.addedNodes &&
        r.addedNodes.forEach((n) => {
          if (
            n.nodeType === 1 &&
            n.querySelector &&
            (n.matches?.('.product-card-modern') || n.querySelector('.product-card-modern'))
          ) {
            needs = true;
          }
        });
    });
    if (needs) reinitWithin(document);
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });

  /* ==============================
     Events
  ============================== */
  // Dynamic option click (ALL option types)
  document.addEventListener('click', (e) => {
    const optBtn = e.target.closest('.product-card-modern .option-value');
    if (optBtn) {
      const card = optBtn.closest('.product-card-modern');
      const holder = card.querySelector('.modern-options');
      if (!holder) return;

      const options = parseJSON(holder, '.product-options') || [];
      const variants = parseJSON(holder, '.product-variants') || [];
      if (!options.length || !variants.length) return;

      // update active in its group
      const group = optBtn.closest('.option-group');
      group.querySelectorAll('.option-value').forEach((b) => b.classList.remove('is-active'));
      optBtn.classList.add('is-active');

      // update selection
      const idx = Number(optBtn.dataset.optionIndex);
      const value = optBtn.dataset.optionValue;
      const selection = holder._selection || {};
      selection[idx] = value;
      holder._selection = selection;

      // recompute availability
      applyAvailability(card, options, variants, selection);

      // resolve & reflect variant
      const variant = resolveVariantForSelection(variants, selection);
      // On user interaction: update BOTH primary and secondary
      updateImageForVariant(card, variant, { alsoSecondary: true });
      updateAddToCartForVariant(card, variant);
      // NEW: switch from “From …” to the selected variant’s exact price
      updatePriceForVariant(card, variant);
      return;
    }
  });

  function updatePriceForVariant(card, variant) {
    const wrap = card.querySelector('.modern-price');
    if (!wrap || !variant) return;

    const priceEl = wrap.querySelector('.price');
    const regularWrap = priceEl?.querySelector('.price__regular');
    const saleWrap = priceEl?.querySelector('.price__sale');

    const regularSpan = regularWrap?.querySelector('.price-item--regular'); // regular price holder
    const saleSpan = saleWrap?.querySelector('.price-item--sale'); // sale price holder
    const strikeSpan = saleWrap?.querySelector('s.price-item--regular'); // crossed compare_at

    const available = !!variant.available;
    const price = Number(variant.price || 0);
    const compareAt = Number(variant.compare_at_price || 0);
    const onSale = compareAt > price && price > 0;

    // Choose which preformatted strings to use (match Dawn setting)
    const withCode = wrap.dataset.useCode === 'true';
    const fmt = {
      price: withCode ? variant.money_with_currency : variant.money,
      compare: withCode ? variant.compare_money_with_currency : variant.compare_money,
    };

    // Mirror Dawn state classes (CSS handles which block shows)
    if (priceEl) {
      priceEl.classList.toggle('price--sold-out', !available);
      priceEl.classList.toggle('price--on-sale', onSale);
    }

    if (onSale) {
      if (strikeSpan) strikeSpan.textContent = fmt.compare || '';
      if (saleSpan) saleSpan.textContent = fmt.price || '';
    } else {
      if (regularSpan) regularSpan.textContent = fmt.price || '';
    }

    // If your "From …" label exists in the regular block, remove once a variant is chosen
    const fromLabel = regularWrap?.querySelector('[data-price-from]');
    if (fromLabel) fromLabel.remove();
  }

  // Intercept in-card forms for AJAX add
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('.product-card-modern form.modern-form');
    if (!form) return;
    e.preventDefault();
    const input = form.querySelector('.js-variant-id');
    const btn = form.querySelector('.modern-add');
    if (!input?.value) return;
    await addToCartAjax(input.value, 1, btn);
  });

  // Simple ajax add button (no form)
  document.addEventListener('click', async (e) => {
    const ajaxBtn = e.target.closest('.product-card-modern .js-ajax-add');
    if (!ajaxBtn) return;
    const vid =
      ajaxBtn.getAttribute('data-variant-id') ||
      ajaxBtn.closest('.product-card-modern')?.querySelector('.js-variant-id')?.value;
    if (!vid) return;
    await addToCartAjax(vid, 1, ajaxBtn);
  });
  document.addEventListener(
    'mouseover',
    function (e) {
      const card = e.target.closest('.product-card-modern.card--modern');
      if (!card) return;

      const li = card.closest('li');
      if (!li || li.dataset.revealCleaned) return;

      // Remove Dawn’s reveal classes
      li.classList.remove('scroll-trigger');
      for (const c of Array.from(li.classList)) {
        if (c.startsWith('animate--')) li.classList.remove(c);
      }
      li.style.removeProperty('--animation-order');

      li.dataset.revealCleaned = '1';
    },
    { passive: true }
  );
  // External refresh signal support
  document.addEventListener('cart:refresh', () => {
    if (typeof window.refreshCartUI === 'function') {
      window.refreshCartUI({ open: false });
    }
  });

  function expandPanel(panel) {
    // Reset to measure natural height
    panel.style.maxHeight = 'none';
    const target = panel.scrollHeight;

    // From 0 → target
    panel.style.maxHeight = '0px';
    panel.offsetHeight; // force reflow
    panel.style.maxHeight = target + 'px';
    panel.style.opacity = '1';
    panel.setAttribute('aria-hidden', 'false');
    panel.removeAttribute('inert');

    // After transition, allow natural growth
    const onEnd = (e) => {
      if (e.propertyName === 'max-height') {
        panel.style.maxHeight = 'none';
        panel.removeEventListener('transitionend', onEnd);
      }
    };
    panel.addEventListener('transitionend', onEnd);
  }

  function collapsePanel(panel) {
    // From current height → 0
    if (getComputedStyle(panel).maxHeight === 'none') {
      panel.style.maxHeight = panel.scrollHeight + 'px';
    }
    panel.offsetHeight; // reflow
    panel.style.maxHeight = '0px';
    panel.style.opacity = '0';
    panel.setAttribute('aria-hidden', 'true');
    panel.setAttribute('inert', '');
  }

  // Mobile quick-add toggle (single, robust handler)
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('.mobile-quick-toggle');
    if (toggle) {
      e.preventDefault();

      const card = toggle.closest('.product-card-modern');
      const panel = card?.querySelector('.modern-hover-panel');
      if (!card || !panel) return;

      // Close others
      document.querySelectorAll('.product-card-modern.is-mobile-open').forEach((c) => {
        if (c === card) return;
        c.classList.remove('is-mobile-open');
        c.querySelector('.mobile-quick-toggle')?.setAttribute('aria-expanded', 'false');
        const p = c.querySelector('.modern-hover-panel');
        if (p) collapsePanel(p);
      });

      const opening = !card.classList.contains('is-mobile-open');
      card.classList.toggle('is-mobile-open', opening);
      toggle.setAttribute('aria-expanded', opening ? 'true' : 'false');

      if (opening) {
        expandPanel(panel);
      } else {
        collapsePanel(panel);
      }
      return;
    }

    // Clicked outside any product card → close all
    if (!e.target.closest('.product-card-modern')) {
      document.querySelectorAll('.product-card-modern.is-mobile-open').forEach((c) => {
        c.classList.remove('is-mobile-open');
        c.querySelector('.mobile-quick-toggle')?.setAttribute('aria-expanded', 'false');
        const p = c.querySelector('.modern-hover-panel');
        if (p) collapsePanel(p);
      });
    }
  });

  /* === Dynamic hover extension (measure panel height) === */
  // Helper: is this a touch / no-hover device?
  const IS_TOUCH = window.matchMedia('(hover: none), (pointer: coarse)').matches;

  /* === Dynamic hover extension (measure panel height) === */
  if (!IS_TOUCH) {
    document.addEventListener(
      'mouseenter',
      (e) => {
        const card = e.target.closest?.('.product-card-modern.card--modern');
        if (!card) return;
        const panel = card.querySelector('.modern-hover-panel');
        if (!panel) return;
        // A11Y: make visible panel perceivable & focusable
        panel.setAttribute('aria-hidden', 'false');
        panel.removeAttribute('inert');
        const prevPos = panel.style.position;
        const prevLeft = panel.style.left;
        const prevRight = panel.style.right;
        const prevBottom = panel.style.bottom;
        const prevOpacity = panel.style.opacity;
        const prevTransform = panel.style.transform;
        const prevPointer = panel.style.pointerEvents;

        panel.style.position = 'absolute';
        panel.style.left = '-9999px';
        panel.style.right = 'auto';
        panel.style.bottom = 'auto';
        panel.style.opacity = '1';
        panel.style.transform = 'none';
        panel.style.pointerEvents = 'none';

        const needed = Math.ceil(panel.scrollHeight);

        panel.style.position = prevPos;
        panel.style.left = prevLeft;
        panel.style.right = prevRight;
        panel.style.bottom = prevBottom;
        panel.style.opacity = prevOpacity;
        panel.style.transform = prevTransform;
        panel.style.pointerEvents = prevPointer;

        card.style.setProperty('--hover-extend', needed + 'px');
      },
      true
    );
  }

  /* ==============================
     Main Product Page Variant Picker
  ============================== */

  window.initModernVariantPicker = function(container) {
    if (!container || container.dataset.init === 'true') return;
    container.dataset.init = 'true';

    const optionsData = parseJSON(container, '.product-options');
    const variantsData = parseJSON(container, '.product-variants');
    if (!optionsData?.length || !variantsData?.length) return;

    const swatchList = readSwatchLines(container);
    const groupsContainer = container.querySelector('.modern-option-groups');
    const selection = {};

    // Initialize with current selection
    const selectedVariant = parseJSON(container, '[data-selected-variant]');
    if (selectedVariant?.options) {
      selectedVariant.options.forEach((val, i) => {
        selection[i] = val;
      });
    }

    function renderOptions() {
      if (!groupsContainer) return;

      groupsContainer.innerHTML = '';
      const avail = computeAvailabilityMatrix(optionsData, variantsData, selection);

      // Sort options: Color first, then others
      const sortedOptions = optionsData.map((name, index) => ({ name, index }));
      sortedOptions.sort((a, b) => {
        const aIsColor = a.name.toLowerCase().includes('color');
        const bIsColor = b.name.toLowerCase().includes('color');
        if (aIsColor && !bIsColor) return -1;
        if (!aIsColor && bIsColor) return 1;
        return 0;
      });

      sortedOptions.forEach(({ name, index }) => {
        const uniques = buildUniques(optionsData, variantsData);
        const values = uniques[index] || [];
        if (!values.length) return;

        const isColor = name.toLowerCase().includes('color');

        const group = document.createElement('div');
        group.className = 'option-group';
        group.innerHTML = `
          <label class="option-label">${name}</label>
          <div class="option-values" data-option-index="${index}"></div>
        `;

        const valuesContainer = group.querySelector('.option-values');

        values.forEach(value => {
          const isSelected = selection[index] === value;
          const isAvailable = avail[index][value];

          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = `option-value ${isColor ? 'is-color' : ''} ${isSelected ? 'is-active' : ''} ${!isAvailable ? 'is-disabled' : ''}`;
          btn.dataset.value = value;
          btn.dataset.optionIndex = index;

          if (isColor) {
            // Color swatch
            const swatch = findSwatchByName(swatchList, value);
            if (swatch?.hex) {
              btn.style.setProperty('--swatch-bg', swatch.hex);
              btn.style.background = swatch.hex;
            } else if (swatch?.image) {
              btn.style.setProperty('--swatch-bg', `url(${swatch.image})`);
              btn.style.backgroundImage = `url(${swatch.image})`;
              btn.style.backgroundSize = 'cover';
              btn.style.backgroundPosition = 'center';
            } else {
              // Try to find variant image for this color
              const variantImage = findVariantImageForOptionValue(variantsData, index, value);
              if (variantImage) {
                btn.style.backgroundImage = `url(${variantImage})`;
                btn.style.backgroundSize = 'cover';
                btn.style.backgroundPosition = 'center';
              } else {
                btn.style.background = '#ddd';
              }
            }
            btn.innerHTML = `<span class="label-text">${value}</span>`;
          } else {
            // Regular pill button
            btn.textContent = value;
          }

          btn.addEventListener('click', () => {
            if (!isAvailable) return;

            selection[index] = value;

            // Update hidden select
            const hiddenSelect = container.querySelector(`.modern-select-${index}`);
            if (hiddenSelect) {
              hiddenSelect.value = value;
              hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }

            // Find and update variant
            const newVariant = resolveVariantForSelection(variantsData, selection);
            if (newVariant) {
              // Update URL if needed
              const url = new URL(window.location);
              url.searchParams.set('variant', newVariant.id);
              window.history.replaceState({}, '', url);

              // Update product media if on main product page
              updateMainProductMedia(newVariant);

              // Update main product form variant ID
              const mainForm = document.querySelector('product-form form');
              if (mainForm) {
                const variantInput = mainForm.querySelector('input[name="id"]');
                if (variantInput) {
                  variantInput.value = newVariant.id;
                  variantInput.dispatchEvent(new Event('change', { bubbles: true }));
                }
              }

              // Update price displays
              updateProductPrice(newVariant);

              // Dispatch multiple variant change events for compatibility
              document.dispatchEvent(new CustomEvent('variant:change', {
                detail: { variant: newVariant }
              }));

              document.dispatchEvent(new CustomEvent('product:variant-change', {
                detail: { variant: newVariant }
              }));

              // Trigger form change event
              if (mainForm) {
                mainForm.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }

            renderOptions();
          });

          valuesContainer.appendChild(btn);
        });

        groupsContainer.appendChild(group);
      });
    }

    function updateMainProductMedia(variant) {
      if (!variant?.image) return;

      // Update main product images
      const mainImage = document.querySelector('.product__media img');
      const galleryImages = document.querySelectorAll('.product__media-list img');

      if (mainImage && variant.image) {
        mainImage.src = variant.image;
        mainImage.srcset = '';
      }

      // If there's a gallery, try to find matching image
      if (variant.media_id) {
        galleryImages.forEach(img => {
          const mediaId = img.closest('[data-media-id]')?.dataset?.mediaId;
          if (mediaId == variant.media_id) {
            // Move this image to the front or focus it
            const mediaItem = img.closest('.product__media-item');
            if (mediaItem) {
              mediaItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          }
        });
      }
    }

    function updateProductPrice(variant) {
      if (!variant) return;

      // First, try to use Shopify's native price update system
      const productInfo = document.querySelector('product-info');
      if (productInfo) {
        // Trigger Shopify's native variant change
        const variantInput = document.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = variant.id;
          variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Also trigger on the product-info element
        productInfo.dispatchEvent(new CustomEvent('variant:change', {
          detail: { variant: variant }
        }));
      }

      // Force update all price elements that might contain the price
      const allPriceSelectors = [
        '.price__sale .price-item--sale.price-item--last',
        '.price__regular .price-item--regular',
        '.price-item--sale.price-item--last',
        '.price-item--regular',
        '.price .money',
        '.current-price',
        '#price-' + (productInfo?.dataset?.section || '') + ' .price-item--sale.price-item--last',
        '#price-' + (productInfo?.dataset?.section || '') + ' .price-item--regular'
      ];

      // Update ALL matching price elements (not just the first one)
      allPriceSelectors.forEach(selector => {
        if (selector.includes('undefined')) return; // Skip invalid selectors

        const priceElements = document.querySelectorAll(selector);
        priceElements.forEach(priceEl => {
          if (priceEl && variant.money && !priceEl.closest('.sticky-nav')) {
            // Only update if it's not a sticky nav element (to avoid conflicts)
            priceEl.textContent = variant.money;
          }
        });
      });

      // Also try updating by section ID specifically
      const sectionId = container.dataset.section;
      if (sectionId) {
        const sectionPriceElements = document.querySelectorAll(`#price-${sectionId} .price-item`);
        sectionPriceElements.forEach(el => {
          if (el.classList.contains('price-item--sale') && el.classList.contains('price-item--last')) {
            el.textContent = variant.money;
          } else if (el.classList.contains('price-item--regular') && !el.closest('.price__sale')) {
            el.textContent = variant.money;
          }
        });
      }

      // Update compare at price if exists
      if (variant.compare_at_price && variant.compare_money) {
        const compareElements = document.querySelectorAll('.price__regular .price-item--regular, .compare-at-price');
        compareElements.forEach(el => {
          if (el.textContent.trim()) {
            el.textContent = variant.compare_money;
          }
        });
      }

      // Update sticky bar price (if it exists)
      const stickyPrice = document.querySelector('.sticky-add-btn .btn-price');
      if (stickyPrice && variant.money) {
        stickyPrice.textContent = variant.money;
      }

      // Update any data attributes
      const priceContainer = document.querySelector('.price');
      if (priceContainer) {
        priceContainer.setAttribute('data-current-price', variant.price);
      }

      // Force a re-render of any price components
      document.dispatchEvent(new CustomEvent('price:update', {
        detail: { variant: variant }
      }));

      // Force a delayed update to ensure all systems catch the change
      setTimeout(() => {
        // Double-check price update after a brief delay
        const mainPriceEl = document.querySelector('.price__sale .price-item--sale.price-item--last') ||
                            document.querySelector('.price__regular .price-item--regular') ||
                            document.querySelector('.price-item--sale.price-item--last');

        if (mainPriceEl && variant.money && mainPriceEl.textContent !== variant.money) {
          mainPriceEl.textContent = variant.money;
          // Force a visual refresh
          mainPriceEl.style.opacity = '0.99';
          setTimeout(() => {
            mainPriceEl.style.opacity = '';
          }, 10);
        }
      }, 100);
    }

    // Initial render
    renderOptions();

    // Listen for external variant changes (from URL, etc.)
    document.addEventListener('variant:change', (e) => {
      if (e.detail?.variant?.options) {
        e.detail.variant.options.forEach((val, i) => {
          selection[i] = val;
        });
        renderOptions();
      }
    });
  };
})();
